import { makeObservable, observable, action, runInAction, toJS } from "mobx";
import { SearchRequest } from "../api/Types";
import {
  PerPageSettings,
  WithBooleanFlag,
  Sorter,
  Searcher,
  Filter,
  ExpandableKeys,
  StatusInfo,
} from "./core";
import api from "../modules/Admin/api";
import Constants from "../constants/Common";

class UsersStore {
  usersData = [];

  userParamsRowKeys: ExpandableKeys = new ExpandableKeys();
  isShowAddForm = new WithBooleanFlag(false);

  isLoading = new WithBooleanFlag();
  userFormIsSending = new WithBooleanFlag();
  paginator = new PerPageSettings();
  sorter = new Sorter();
  filter: Filter = new Filter();

  search = new Searcher();

  userStateStatus = new StatusInfo();
  roles = [];

  constructor() {
    makeObservable(
      this,
      {
        usersData: observable,
        roles: observable,
        getUsersList: action,
      },
      {
        deep: true,
      },
    );
  }

  getUsersList = async (forceUpdate = true) => {
    this.isLoading.setTrue();
    if (!forceUpdate && this.usersData.length) {
      return;
    }

    try {
      const request: SearchRequest = {
        page: this.paginator.page,
        count: this.paginator.perPage,
        sortItems: this.sorter.sortData,
        filterItems: this.filter.filterItems,
        search: this.search.getSearch(),
      };
      const res = await api.getUsers(request);
      runInAction(() => {
        this.usersData = res.data.items;
        this.paginator.updateMeta(res.data);
      });
    } catch (e) {
      console.log(e);
    } finally {
      this.isLoading.setFalse();
    }
    return toJS(this.usersData);
  };

  

  getRolesList = async (forceUpdate = true) => {
    function compare(a, b) {
      if (a.name < b.name) {
        return -1; // a меньше b по полю name
      }
      if (a.name > b.name) {
        return 1; // a больше b по полю name
      }
      return 0; // a равно b по полю name
    }
    if (!forceUpdate && this.roles.length) {
      return;
    }

    try {
      const res = await api.getRolesList();
      runInAction(() => {
        this.roles = (res.data?.map((i) => ({
          id: i.id,
          name: i.name,
          isDeleted: i.isDeleted,
        }))).sort(compare);
      });
    } catch (e) {
      console.log(e);
    }
  };

  saveUser = async (values) => {
    try {
      const res = await api.saveUser(values);
      runInAction(() => {
        this.userStateStatus.setStatus(
          !values.id
            ? Constants.requestStatuses.created
            : Constants.requestStatuses.updated,
        );
        this.getUsersList();
      });
      if (values.id) {
        this.userParamsRowKeys.remove(values.id);
      } else {
        this.isShowAddForm.toggle();
      }
    } catch (e) {
      this.userStateStatus.setDetails(e);
      this.userStateStatus.setStatus(Constants.requestStatuses.error);
    }
  };

  deleteUser = async (userId) => {
    try {
      const res = await api.deleteUser(userId);
      runInAction(() => {
        this.userStateStatus.setStatus(Constants.requestStatuses.updated);
        this.getUsersList();
      });
    } catch (e) {
      this.userStateStatus.setStatus(Constants.requestStatuses.error);
      this.userStateStatus.setDetails(e.response.data?.errors);
    }
  };

  unDeleteUser = async (userId) => {
    try {
      await api.unDeleteUser(userId);
      runInAction(() => {
        this.userStateStatus.setStatus('Учетная запись восстановлена');
        this.getUsersList();
      });
    } catch (e) {
      this.userStateStatus.setStatus(Constants.requestStatuses.error);
      this.userStateStatus.setDetails(e.response.data?.errors);
    }
  };

  unlockUser = async (userId) => {
    try {
      const res = await api.unlockUser(userId);
      runInAction(() => {
        this.userStateStatus.setStatus(Constants.requestStatuses.updated);
        this.getUsersList();
      });
    } catch (e) {
      this.userStateStatus.setStatus(Constants.requestStatuses.error);
      this.userStateStatus.setDetails(e.response.data?.errors);
    }
  };

  lockUser = async (userId, lockoutEnd) => {
    try {
      const res = await api.lockUser(userId, lockoutEnd);
      runInAction(() => {
        this.userStateStatus.setStatus(Constants.requestStatuses.updated);
        this.getUsersList();
      });
    } catch (e) {
      this.userStateStatus.setStatus(Constants.requestStatuses.error);
      this.userStateStatus.setDetails(e.response.data?.errors);
    }
  };
}

const userStore = new UsersStore();
export default userStore;
